import type { Messages } from '@/i18n/types';

export const zhCN: Messages = {
  meta: {
    title: 'Fustar — 所思·所见 · 本地优先的 AI 知识中枢',
    description:
      '本地优先的 AI 知识中枢：文件、Agent 与插件，汇于一处，皆成所见。本地运行，数据不出设备。',
  },
  header: {
    tagline: '本地优先的 AI 知识中枢',
  },
  language: {
    switchLabel: '语言',
    zh: '中文',
    en: 'EN',
  },
  theme: {
    switchToLight: '切换到浅色主题',
    switchToDark: '切换到深色主题',
  },
  hero: {
    brandTitle: '所思·所见',
    subtitle: '本地优先的 AI 知识中枢——文件是一等公民，Agent 调度插件，成果皆成所见。',
    featureTags: ['本地优先', '文件一等公民', '万物皆插件', 'Agent 调度插件'],
    downloadCta: '开始使用',
    exploreCta: '了解工作方式',
    previewLabel: '预览版',
  },
  navigation: {
    workspace: '首页',
    capabilities: '核心能力',
    demos: '使用场景',
    stats: '数据指标',
    faq: '常见问题',
    download: '下载',
  },
  useCaseDemos: {
    eyebrow: '万物皆插件',
    title: '按需组合，场景由你定义',
    description:
      'Fustar 通过 Fusion Hub 插件扩展分析与视图能力。按你的场景安装插件，Fustar 负责设计、组织、实现，并沉淀到你的工作台。',
    sortLabel: '排序',
    videoPlaceholder: '观看演示',
    sortOptions: {
      featured: '推荐',
      plugin: '按插件类型',
      recent: '最新',
    },
    items: {
      'cohort-retention': {
        category: '数据分析',
        title: '用户留存与流失分析',
        description:
          '导入 retention.csv，Agent 调用 DuckDB 插件聚合 D+7 激活率，将图表与结论沉淀为文档内容块。',
        author: '增长分析',
      },
      'pdf-semantic-search': {
        category: '文档检索',
        title: 'PDF 长文档语义搜索与引用',
        description:
          '安装语义检索插件后，对数百页 PDF 做自然语言提问，答案带来源段落，可直接写入工作文档。',
        author: '知识管理',
      },
      'sql-explore-visualize': {
        category: 'SQL 探索',
        title: '用自然语言查询数据并生成图表',
        description:
          '用自然语言描述你想看的指标，Agent 在本地查询数据并生成可交互图表，整个过程留在文档中可追溯。',
        author: '数据探索',
      },
      'embed-files': {
        category: '知识中枢',
        title: '文档中嵌入任意文件',
        description:
          '文档、3D、表格、代码皆可嵌入同一文档，Agent 直接引用、对话并生成结果，打破文档与文件的边界。',
        author: '知识中枢',
      },
      'interactive-dashboard': {
        category: '可视化',
        title: '销售数据交互式仪表盘',
        description:
          'Widget 契约将查询结果渲染为可刷新的图表块——筛选、钻取、修改参数后重新运行，过程可追溯。',
        author: '业务看板',
      },
      'research-corpus': {
        category: '应用开发',
        title: '从想法到可运行的应用',
        description:
          '用自然语言描述需求，Agent 生成代码、调用构建插件，将想法编译为可运行的应用与文档。',
        author: '编程助手',
      },
      'file-format-convert': {
        category: '格式转换',
        title: '文件格式自动转换',
        description:
          '将 CSV、Markdown、JSON、图片等文件拖入文档，Agent 自动识别并转换格式，结果可直接复用或导出。',
        author: '格式转换',
      },
      'web-data-query': {
        category: '联网查询',
        title: '联网数据查询',
        description:
          'Agent 调用联网检索插件获取实时信息，将查询结果带来源地写入文档，让结论可追溯、可验证。',
        author: '联网检索',
      },
      'agent-orchestration': {
        category: 'Agent 编排',
        title: '多步骤任务自动编排',
        description:
          '用自然语言描述目标，Agent 依次调用加载、清洗、计算与出图插件，每步推理与结果写回文档。',
        author: '工作流',
      },
      'hub-plugin-install': {
        category: '插件生态',
        title: '从 Hub 按需安装领域插件',
        description:
          '打开 Fusion Hub，浏览智算与可视化插件，一键安装后即可在 Agent 与命令面板中调用，扩展你的场景边界。',
        author: 'Fusion Hub',
      },
    },
  },
  workspace: {
    eyebrow: '从文档到成果',
    title: '撰写文档、运行 Agent、留存成果，在同一工作空间完成。',
    description:
      '将资料、任务说明与 Agent 编排集中于同一工作空间。每一次分析结果均可重新打开、验证与延续。',
    previewLabel: '交互预览',
    runCta: '运行 Agent',
    resetCta: '重新运行',
    readyStatus: '等待运行',
    computingStatus: 'Agent 正在本地编排',
    storedStatus: '结果已写入文档',
    visualLabel: '可视化',
    computeLabel: '可计算',
    storageLabel: '可存储',
    queryLabel: '分析任务',
    query: '对比 cohort 在第 7 天的激活率，找出流失前的共同信号',
    tableLabel: '结果数据',
    noteLabel: '工作文档',
    noteTitle: '用户留存分析',
    noteBody: '观察新用户在前七天的激活路径，并找出流失前的共同信号。',
    agentLabel: 'Agent 任务',
    agentTitle: '正在编排分析与出图',
    agentBody: '解析任务目标，调用工具链，验证结果，并将过程写回当前文档。',
    insightLabel: '关键发现',
    insightTitle: '激活率',
    insightMetric: '+18.4%',
    insightDetail: '完成首个项目的用户，在第 30 天的留存更高。',
    dataSource: 'retention.csv · 12.4k rows',
    computeLogs: ['加载 cohort 表', '聚合 D+7 激活率', '写入 vision block'],
    sidebarNotes: '文档',
    sidebarSources: '数据源',
    sidebarProjects: '项目',
  },
  capabilities: {
    eyebrow: '核心能力',
    title: '看见、计算与留下，在同一工作空间内完成。',
    description:
      '从文档撰写到 Agent 编排再到成果沉淀，形成可延续的工作流；输出不是一次性结果，而是下一轮分析的起点。',
    items: [
      {
        eyebrow: 'Visualize',
        title: '多样载体，交互呈现',
        description:
          '文档、表格、音视频与科学视图——无论载体为何，都可变成可交互的视觉块，看见数据中的结构与关系。',
      },
      {
        eyebrow: 'Compute',
        title: '本地即可计算与编排',
        description:
          '用自然语言驱动 Agent 编排，从 SQL 探索、统计检验到 Hub 领域插件，过程与参数随结果一起保留，而不只给出一个答案。',
      },
      {
        eyebrow: 'Store',
        title: '成果块级沉淀',
        description:
          '图表、交互式表格与结论作为活的内容块沉淀在项目里，可重新打开、编辑、复现与继续生长。',
      },
    ],
  },
  stats: {
    eyebrow: '数据与指标',
    title: '轻量、快速、隐私优先',
    description: 'Fustar 在本地运行，数据不出设备，同时保持出色的性能表现。',
    items: [
      {
        value: '< 150 MB',
        label: '安装体积',
        description: '桌面客户端小巧精悍，秒级下载安装',
      },
      {
        value: '本地优先',
        label: '桌面端完整能力',
        description:
          '工作空间、附件分析与 Hub 智算在桌面版于本机完成；在线版为功能受限的预览',
      },
      {
        value: '任意格式',
        label: '文件格式',
        description: '支持 CSV、Parquet、JSON、Markdown 等常见格式',
      },
      {
        value: '插件化',
        label: '可扩展架构',
        description: '通过 Hub 插件市场扩展分析能力与视图类型',
      },
    ],
  },
  download: {
    eyebrow: '从桌面开始',
    title: '为知识工作提供完整的工作空间。',
    description: 'Fustar 支持 Windows、macOS、Linux 和在线体验。选一个适合你的方式开始。',
  },
  openSource: {
    eyebrow: '可追溯 · 开放生态',
    title: '过程可追溯，插件逐步开源',
    description:
      'Fustar 的每一步分析行为均可追溯，计算过程与参数随结果一同保留。Fusion Hub 中的智算与可视化插件，我们计划逐步以开源形式发布，便于社区共建与审计。你的数据始终留在本地，不会用于训练模型，也不会被上传到第三方。',
    badge: '插件开源计划中',
    cta: '在 GitHub 关注',
  },
  platforms: {
    windows: { label: 'Windows', hint: 'Windows 10+' },
    'macos-arm64': { label: 'macOS', hint: 'Apple Silicon（M 系列）' },
    'macos-x64': { label: 'macOS', hint: 'Intel 芯片' },
    linux: { label: 'Linux', hint: 'AppImage（arm64）' },
    web: { label: '在线体验', hint: '浏览器版' },
  },
  platformsHover: {
    download: '下载',
    open: '打开',
  },
  features: [
    {
      id: 'local',
      title: '文件是一等公民',
      paragraphs: [
        '文档、表格、代码、3D、数据——任意类型的文件都能进入同一个编辑器，统一承载、统一对话。',
        '不是“上传到别处处理”，而是文件本身就在你的工作台上，成为可计算、可对话、可沉淀的一等对象。',
      ],
    },
    {
      id: 'agent',
      title: '一个 Agent，调度一切插件',
      paragraphs: [
        '读懂你的意图，自动调用合适的插件，把复杂任务拆解、执行、交付——插件中心解决所有场景问题。',
        'Agent 是超级 AI 中心，按需编排插件，让每一次表达都变成可落地的结果。',
      ],
    },
    {
      id: 'vision',
      title: '成果沉淀，可复现',
      paragraphs: [
        '每一次对话的结果，都落回编辑器成为可复用的文档与资产，而非一次性的聊天。',
        '过程、参数与结论一并保留，可重新打开、验证与延续，形成可持续生长的知识。',
      ],
    },
  ],
  illustrations: {
    local: '本地服务器与数据留在本机的示意插图',
    agent: 'Agent 编排与成果写回文档的示意插图',
    vision: '工作文档与内容块沉淀的示意插图',
  },
  faq: {
    eyebrow: '常见问题',
    title: '常见问题',
    items: [
      {
        question: 'Fustar 是完全免费使用的产品吗？是否有功能限制？',
        answer:
          '目前处于 Alpha，可自由体验。正式版后，个人用户仍可免费使用核心能力——文档、工作空间与本地 Agent 编排等，日常写作与轻度分析不设门槛。涉及云端模型调用、高频计算等较消耗资源的操作，将纳入每月免费额度，一般个人使用足够；用量超出时可选择升级。团队与企业方案也在筹备，欢迎通过官网联系我们。',
      },
      {
        question: '我的本地数据和隐私在 Fustar 中是否安全？',
        answer:
          'Fustar 采用本地优先（Local-First）设计。**桌面客户端**默认不将工作空间中的原始文件上传到云端；附件分析与 Hub 智算在设备上的本地环境中运行，我们不会用你的文件训练模型。**在线体验版**在浏览器中运行，不支持完整本地文件系统、容器智算与部分 Agent 工具，适合预览编辑体验。涉及隐私与合规的完整本地能力，请使用桌面应用。',
      },
      {
        question: 'Fustar 目前原生支持哪些数据与文件格式？',
        answer:
          '当前 Alpha 已支持常见的数据与文档附件，包括 CSV、Parquet、TSV、JSON、Markdown、纯文本、PDF，以及 PNG/JPEG/SVG/WebP 等图片。具体能力随已安装的 Hub 插件与 Agent 模式而异（例如 DuckDB 适合结构化表分析，文档语义检索适合 PDF/Markdown 长文）。Excel 等格式通常需通过插件或先转换为 CSV/Markdown 再分析。超大文件的处理能力取决于插件与设备性能，我们会随版本持续扩展格式支持。',
      },
      {
        question: '我可以通过哪些方式自定义扩展 Fustar 的能力边界？',
        answer:
          '当前推荐且已可用的扩展方式主要是 **Fusion Hub 插件**：从 Hub 安装智算或可视化插件后，可在 Agent 与命令面板中调用。出图场景可使用 widget 契约将结果渲染为文档中的图表块。桌面版还可选配 **AI 智算引擎（容器计算）** 运行部分重型插件，需先在应用内完成环境配置。Widget 自定义、命令面板开放 API 与更多无代码组合能力在路线图中，Alpha 阶段请以 Hub 插件为准。',
      },
    ],
  },
  waitlist: {
    eyebrow: '保持关注',
    title: '抢先体验 Fustar',
    description: '使用邮箱验证码登录即可加入候补，获取产品更新与内测通知。',
    placeholder: '输入你的邮箱',
    codePlaceholder: '输入邮箱验证码',
    sendCodeButton: '获取验证码',
    verifyButton: '加入候补',
    resendCode: '重新获取验证码',
    sending: '发送中…',
    verifying: '验证中…',
    error: '操作失败，请检查邮箱与验证码后重试。',
    success: '已加入候补名单！',
  },
  footer: {
    privacy: '原始数据与本地分析留在你的设备。我们不会用你的文件训练模型。',
  },
};
